import { Component, OnInit, ViewChild } from '@angular/core';
import { EnrichedAuditTrailMappingsItem } from './audit-trail-mappings-item.model';
import { Observable } from 'rxjs';
import moment from 'moment';
import {
  Column,
  ExcelExportService,
  FilterService,
  GridComponent,
  GridModule,
  PageService,
  SearchService,
  SortService,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { AuditTrailMappingsService } from './audit-trail-mappings.service';
import { AsyncPipe } from '@angular/common';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { getMappingTypeDisplay, MappingTypePipe } from '../../mappings/mapping-type.enum';
import { CigpStaffAvatarCardComponent } from '../../cigp-staff/cigp-staff-avatar-card.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';

@Component({
  selector: 'eaa-audit-trail-mappings',
  providers: [ExcelExportService, FilterService, PageService, SearchService, SortService, ToolbarService],
  imports: [AsyncPipe, CigpStaffAvatarCardComponent, DateTimePickerModule, GridModule, MappingTypePipe],
  template: `
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 class="card-title">MAPPINGS LOGS</h5>
          <h6 class="card-subtitle text-body-secondary">Audit Trail</h6>
        </div>
        <div class="d-flex align-items-center gap-3">
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="Start"
            floatLabelType="Auto"
            [(value)]="start"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          ></ejs-datetimepicker>
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="End"
            floatLabelType="Auto"
            [(value)]="end"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          ></ejs-datetimepicker>
        </div>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          #grdAuditTrailMappingsItems
          id="grdAuditTrailMappingsItems"
          [dataSource]="items$ | async"
          [allowPaging]="true"
          [pageSettings]="{ pageSize: 50, pageSizes: ['10', '20', '50', '100', '250', 'All'] }"
          [allowFiltering]="true"
          [filterSettings]="{ enableCaseSensitivity: false, ignoreAccent: true, type: 'Excel' }"
          [allowSorting]="true"
          [allowTextWrap]="true"
          [allowExcelExport]="true"
          [toolbar]="['ExcelExport', 'Search']"
          (toolbarClick)="toolbarClick($event)"
        >
          <e-columns>
            <e-column field="id" headerText="#" textAlign="Right" width="40px" format="N0" [isPrimaryKey]="true" />
            <e-column
              field="timestamp"
              headerText="Time"
              type="datetime"
              format="dd/MM/yyyy HH:mm:ss zz"
              width="100px"
            />
            <e-column field="submitter" headerText="Author" width="100px">
              <ng-template #template let-data>
                <eaa-cigp-staff-avatar-card
                  [user]="data.submitter"
                  [showInitials]="true"
                  [fallbackValue]="data.submitterEmail"
                />
              </ng-template>
            </e-column>
            <e-column field="action" headerText="Action" width="80px" />
            <e-column field="portfolioName" headerText="Account" width="90px" />
            <e-column field="mappingType" headerText="Mapping Type" width="80px">
              <ng-template #template let-data>
                <span>{{ data.mappingType | mappingType }}</span>
              </ng-template>
            </e-column>
            <e-column headerText="Client / Staff" width="100px">
              <ng-template #template let-data>
                @if (data.mappingType === 'CigpStaff') {
                  <eaa-cigp-staff-avatar-card
                    [user]="data.cigpStaff"
                    [showInitials]="true"
                    [fallbackValue]="data.cigpStaffEmail"
                  />
                } @else {
                  <span [title]="data.clientEmail">{{ data.client?.displayName || data.clientEmail }}</span>
                }
              </ng-template>
            </e-column>
          </e-columns>
        </ejs-grid>
      </div>
    </div>
  `
})
export class AuditTrailMappingsComponent implements OnInit {
  public items$: Observable<EnrichedAuditTrailMappingsItem[]> | null = null;
  public today = new Date();
  public searching = false;
  public start = moment().subtract(7, 'days').toDate();
  public end = new Date();

  @ViewChild('grdAuditTrailMappingsItems')
  public grid?: GridComponent;

  constructor(private auditTrailMappingsService: AuditTrailMappingsService) {}

  public ngOnInit(): void {
    this.setAuditTrailItems();
  }

  public dateRangeSelected(): void {
    this.searching = true;

    this.setAuditTrailItems();

    setTimeout(() => {
      this.searching = false;
    }, 500);
  }

  public toolbarClick(args: ClickEventArgs): void {
    if (args.item.id === 'grdAuditTrailMappingsItems_excelexport') {
      this.grid?.excelExport({
        columns: [
          { field: 'id', headerText: '#' } as Column,
          { field: 'timestamp', headerText: 'Time', format: 'dd/MM/yyyy HH:mm:ss zz' } as Column,
          { field: 'submitter.displayName', headerText: 'Author' } as Column,
          { field: 'submitterEmail', headerText: 'Author (Email)' } as Column,
          { field: 'action', headerText: 'Action' } as Column,
          { field: 'portfolioName', headerText: 'Account' } as Column,
          {
            field: 'mappingType',
            headerText: 'Mapping Type',
            valueAccessor: (_: string, mapping: EnrichedAuditTrailMappingsItem) =>
              getMappingTypeDisplay(mapping.mappingType)
          } as unknown as Column,
          { field: 'client.displayName', headerText: 'Client' } as Column,
          { field: 'clientEmail', headerText: 'Client (Email)' } as Column,
          { field: 'cigpStaff.displayName', headerText: 'Staff' } as Column,
          { field: 'cigpStaffEmail', headerText: 'Staff (Email)' } as Column
        ],
        exportType: 'AllPages',
        fileName: 'Mappings Audit Trail.xlsx'
      });
    }
  }

  private setAuditTrailItems() {
    this.items$ = this.auditTrailMappingsService.getAuditTrailMappingsItems$(this.start, this.end);
  }
}
